import type { AddressForm, LoadOptionChoice, WarehouseOption } from './types';

/**
 * The warehouse list under the carrier-rate table: a name search, plus an
 * optional "only ones I have configured" filter. A rate counts as configured
 * once anything non-blank is typed, before it is valid or saved.
 */
export const filterWarehouses = <T extends WarehouseOption>(
  warehouses: T[],
  form: AddressForm,
  search: string,
  showConfiguredOnly: boolean
): T[] => {
  const normalizedSearch = search.trim().toLowerCase();

  return (warehouses || []).filter((warehouse) => {
    const currentValue = form.carrierRates?.[warehouse.id] ?? '';
    const isConfigured = String(currentValue).trim() !== '';

    if (showConfiguredOnly && !isConfigured) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    const searchableValue = [warehouse.name].filter(Boolean).join(' ').toLowerCase();

    return searchableValue.includes(normalizedSearch);
  });
};

/**
 * Fulfilment methods, selected ones first and alphabetical within each group,
 * then filtered by name or capacity label.
 *
 * Sorting before filtering is deliberate: what is assigned to this location
 * stays at the top of a long org-wide list, so an admin can see the current
 * selection without scrolling for it.
 */
export const sortAndFilterLoadOptions = <T extends LoadOptionChoice>(
  loadOptions: T[],
  form: AddressForm,
  search: string
): T[] => {
  const normalizedSearch = search.trim().toLowerCase();

  const sorted = [...(loadOptions || [])].sort((a, b) => {
    const aSelected = form.orgLoadOptionIds.includes(a.id);
    const bSelected = form.orgLoadOptionIds.includes(b.id);

    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;

    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  });

  if (!normalizedSearch) {
    return sorted;
  }

  return sorted.filter((option) => {
    const searchableValue = [option.name, option.capacityLabel]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableValue.includes(normalizedSearch);
  });
};
